import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RxIconModule } from '@nrwl/rx-icon';
import { RxIntercomService } from '../../intercom/services/rx-intercom.service';
import { RxIntercomLifecycleStore } from '../../intercom/stores/rx-intercom-lifecycle.store';

export type SupportMenuAction = 'start-chat' | 'cookie-preferences';

export interface SupportMenuItem {
  /** Tooltip text shown on hover. */
  label: string;
  /** Icon name from shared-assets/icons/ (without .svg extension). */
  icon: string;
  /** Action to perform when the button is clicked. */
  action: SupportMenuAction;
  resolveState?: (context: Readonly<SupportMenuContext>) => SupportActionState;
}

export type SupportActionStatus = 'enabled' | 'disabled' | 'hidden';

export interface SupportActionState {
  status: SupportActionStatus;
  unavailableTooltip?: string;
}

export interface SupportMenuContext {
  chatVisible: boolean;
  chatAvailable: boolean;
}

interface ResolvedSupportMenuItem extends SupportMenuItem {
  state: SupportActionState;
}

interface WindowWithCookieSettings extends Window {
  appShowCookieSettings?: () => void;
}

const DEFAULT_ACTION_STATE: SupportActionState = { status: 'enabled' };

/** Default menu items — used when no items input is provided. */
export const DEFAULT_SUPPORT_MENU_ITEMS: readonly SupportMenuItem[] = [
  {
    label: 'Start a chat',
    icon: 'headset-mic',
    action: 'start-chat',
    resolveState: ({ chatVisible, chatAvailable }) => {
      if (!chatVisible) {
        return { status: 'hidden' };
      }

      return chatAvailable
        ? DEFAULT_ACTION_STATE
        : {
            status: 'disabled',
            unavailableTooltip: 'Chat is unavailable on this screen',
          };
    },
  },
  { label: 'Cookie Preferences', icon: 'cookie', action: 'cookie-preferences' },
];

@Component({
  selector: 'app-support-menu',
  imports: [RxIconModule, MatTooltipModule],
  templateUrl: './support-menu.component.html',
  styleUrl: './support-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportMenuComponent {
  private readonly document = inject(DOCUMENT);
  private readonly intercomService = inject(RxIntercomService);
  private readonly lifecycle = inject(RxIntercomLifecycleStore);

  /** Menu items to render. Override to customize the menu buttons. */
  public readonly items = input<readonly SupportMenuItem[]>(DEFAULT_SUPPORT_MENU_ITEMS);

  private readonly context = computed<SupportMenuContext>(() => ({
    chatVisible: this.lifecycle.chatVisible(),
    chatAvailable: this.lifecycle.chatAvailable(),
  }));

  protected readonly visibleItems = computed<ResolvedSupportMenuItem[]>(() => {
    const context = this.context();

    return this.items()
      .map((item) => ({
        ...item,
        state: this.resolveSupportItemState(item, context),
      }))
      .filter((item) => this.isVisibleSupportItem(item));
  });

  protected readonly isOpen = signal(false);

  public toggle(): void {
    this.isOpen.update((open) => !open);
  }

  public handleAction(action: SupportMenuAction): void {
    const item = this.items().find((candidate) => candidate.action === action);

    if (!item || this.resolveSupportItemState(item, this.context()).status !== 'enabled') {
      return;
    }

    this.isOpen.set(false);

    switch (action) {
      case 'start-chat':
        this.intercomService.openChat();
        break;

      case 'cookie-preferences':
        this.openCookiebotWidget();
        break;
    }
  }

  private resolveSupportItemState(
    item: SupportMenuItem,
    context: Readonly<SupportMenuContext>,
  ): SupportActionState {
    return item.resolveState?.(context) ?? DEFAULT_ACTION_STATE;
  }

  private isVisibleSupportItem(item: ResolvedSupportMenuItem): boolean {
    return item.state.status !== 'hidden';
  }

  private openCookiebotWidget(): void {
    const widget = this.document.getElementById('CookiebotWidget');
    const trigger = widget?.querySelector<HTMLElement>('.CookiebotWidget-logo');

    if (trigger && !widget.classList.contains('CookiebotWidget-open')) {
      trigger.click();
    } else if (!widget) {
      (this.document.defaultView as WindowWithCookieSettings | null)?.appShowCookieSettings?.();
    }
  }
}
